import { Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { EventEmitter } from '@angular/core';
import { Component, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { debounceTime } from 'rxjs';
import { ComponentBase } from 'src/app/components/core/_infrastructure/classes/componentBase';
import { ExceptionManager } from 'src/app/components/core/_infrastructure/classes/handlers/exceptionManager';
import { AuthService } from 'src/app/components/core/_infrastructure/classes/services/auth.service';
import { NotificationService } from 'src/app/components/core/_infrastructure/classes/services/notification.service';
import { EnumSiNoTodos, ListSiNoTodos, Tc } from 'src/app/components/core/_infrastructure/types/common';
import { FilterSearchingRestriccionProductosExViewModel } from '../restriccionproductoex.types';

@Component({
    selector: 'app-restriccionproductoex-filter',
    templateUrl: './restriccionproductoex-filter.component.html',
})
export class RestriccionProductosExFilterComponent extends ComponentBase implements OnInit, OnChanges {
    @Input() grupos: Tc[];
    @Input() formas: Tc[];
    @Input() listPais: Tc[];
    @Input() lastFilter: FilterSearchingRestriccionProductosExViewModel;
    @Output() search = new EventEmitter<FilterSearchingRestriccionProductosExViewModel>();
    @Output() cleanForm = new EventEmitter<any>(); // Informa que el formulario se ha reseteado
    @Output() groupSelected = new EventEmitter<number>();

    isCollapsed = false;
    listSiNoTodos = ListSiNoTodos;

    constructor(
        private fb: FormBuilder,
        protected notificacionService: NotificationService,
        protected authService: AuthService,
        protected exceptionHandler: ExceptionManager,
    ) {
        super(authService, notificacionService, exceptionHandler);
    }

    ngOnInit(): void {
        this.createForm();
        this.recoverLastSearch();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.formas?.currentValue && this.lastFilter) {
            // Es posible que en el método "recoverLastSearch" se haya seteado el "Grupo".
            // Esto lanza el trigger que "anula" la "Forma".
            // Aquí restablecemos la "Forma".
            this.setIdForma(this.lastFilter.IdForma);
        }
    }

    private createForm(): void {
        this.form = this.fb.group({
            IdPaisDestino: null,
            IdGrupo: null,
            IdForma: null,
            CodigoEppo: '',
            NombreCientifico: '',
            NombreComun: '',
        });
    }

    /**
     * Recover last search from the previous payload
     */
    private recoverLastSearch(): void {
        if (this.lastFilter) {
            this.form.setValue({
                IdPaisDestino: this.lastFilter.IdPaisDestino,
                IdGrupo: this.lastFilter.IdGrupo,
                IdForma: this.lastFilter.IdForma,
                CodigoEppo: this.lastFilter.CodigoEppo,
                NombreCientifico: this.lastFilter.NombreCientifico,
                NombreComun: this.lastFilter.NombreComun,
            });
            this.form.markAsDirty();

            if (this.lastFilter.IdGrupo) {
                // Para recargar el desplegable de Formas, vinculadas al Grupo.
                this.onSelectGrupo(null);
            }
            setTimeout(() => {
                this.onSearch(null);
            }, 200);
        }

        // As soon as the user changes the "filter", the previous lastFilter pagination values has no sense
        this.form.valueChanges.pipe(debounceTime(500)).subscribe(values => {
            if (values) {
                this.clearPaginationFilter();
            }
        });
    }

    /**
     * Manejador del evento para limpiar el formulario.
     * @param $event Evento javascript
     */
    resetForm($event: any): void {
        $event.stopPropagation();
        this.form.setValue({
            IdPaisDestino: null,
            IdGrupo: null,
            IdForma: null,
            CodigoEppo: '',
            NombreCientifico: '',
            NombreComun: '',
        });
        this.form.markAsPristine();

        this.searchingButton = false;
        this.isCollapsed = false;

        this.cleanForm.emit();
    }

    /**
     * We don't clear the whole filter but only the fields related to the pagination.
     */
    private clearPaginationFilter() {
        if (!this.lastFilter) {
            return;
        }
        this.lastFilter.PageNumber = 0;
        this.lastFilter.Asc = 0;
        this.lastFilter.OrderBy = '';
    }

    /**
     * Manejador del evento de búsqueda
     * @param $event Evento Javascript
     */
    onSearch($event: any): void {
        $event?.stopPropagation();
        this.searchingButton = true;
        this.isCollapsed = true;

        const payload: FilterSearchingRestriccionProductosExViewModel = {
            ...this.form.value,
            PageNumber: this.lastFilter?.PageNumber || 0,
            Asc: this.lastFilter?.Asc || 0,
            OrderBy: this.lastFilter?.OrderBy || '',
        };

        this.search.emit(payload);
    }

    /**
     * Manejador del evento de selección de un grupo en el desplegable.
     * Ignoramos el valor que viene en el parámetro y lo tomamos del form.
     * @param $event Id y descripción del grupo seleccionado
     */
    onSelectGrupo($event: any): void {
        const idGrupo = this.form.value.IdGrupo;
        this.groupSelected.emit(idGrupo);
        this.setIdForma(null);
    }

    setIdForma(idForma: number | null): void {
        this.form.patchValue({
            IdForma: idForma,
        });
    }

    /**
     * Para controlar la propiedad disabled del botón de reset.
     * @returns boolean
     */
    formIsUntouched(): boolean {
        return this.form.pristine || !this.form.dirty;
    }

    /**
     * Para controlar la propiedad disabled del botón de búsqueda.
     * Mientras no estén cargados los grupos, no se habilita el botón.
     * @returns boolean
     */
    get disableSearchBtn(): boolean {
        return this.isCollapsed || !this.grupos || this.grupos?.length == 0 || !this.listPais || this.listPais?.length == 0;
    }
}
